import React, { useState } from 'react';
import { joinWaitlist } from '../api/waitlist';
import './Waitlist.css';

// Icon components for features
const DumbbellIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.4 14.4L9.6 9.6" />
    <path d="M18.657 21.485a2 2 0 1 1-2.829-2.828l-1.767 1.768a2 2 0 1 1-2.829-2.829l6.364-6.364a2 2 0 1 1 2.829 2.828l-1.768 1.768a2 2 0 1 1 2.828 2.828z" />
    <path d="M21.5 21.5l-1.4-1.4" />
    <path d="M3.9 3.9l1.4 1.4" />
    <path d="M6.404 12.768a2 2 0 1 1-2.829-2.829l1.768-1.767a2 2 0 1 1-2.828-2.829l2.828-2.828a2 2 0 1 1 2.829 2.828l1.767-1.768a2 2 0 1 1 2.829 2.829z" />
  </svg>
);

const ChartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="12" y1="20" x2="12" y2="10" />
    <line x1="18" y1="20" x2="18" y2="4" />
    <line x1="6" y1="20" x2="6" y2="16" />
  </svg>
);

const SwordsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M14.5 17.5L3 6V3h3l11.5 11.5" />
    <path d="M13 19l6-6" />
    <path d="M16 16l5 5" />
    <path d="M19 21l2-2" />
    <path d="M14.5 6.5L3 18v3h3l11.5-11.5" />
    <path d="M5 14l4 4" />
    <path d="M7 17l-3 3" />
    <path d="M3 19l2 2" />
  </svg>
);

const HeartIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
  </svg>
);

const TrendingIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polyline points="23 6 13.5 15.5 8.5 10.5 1 18" />
    <polyline points="17 6 23 6 23 12" />
  </svg>
);

const TargetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <circle cx="12" cy="12" r="10" />
    <circle cx="12" cy="12" r="6" />
    <circle cx="12" cy="12" r="2" />
  </svg>
);

export default function Waitlist() {
  const [formData, setFormData] = useState({
    email: '',
    name: '',
    phone: '',
    referralSource: ''
  });
  const [status, setStatus] = useState('idle'); // 'idle', 'loading', 'success', 'error'
  const [errorMessage, setErrorMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState({
    email: '',
    name: '',
    phone: ''
  });
  const [touched, setTouched] = useState({
    email: false,
    name: false,
    phone: false
  });

  // Validation functions
  const validateEmail = (email) => {
    if (!email) {
      return 'Email is required';
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      return 'Please enter a valid email address';
    }
    return '';
  };

  const validateName = (name) => {
    if (name && name.length > 0 && name.length < 2) {
      return 'Name must be at least 2 characters';
    }
    if (name && name.length > 100) {
      return 'Name must be less than 100 characters';
    }
    if (name && !/^[a-zA-Z\s\-']+$/.test(name)) {
      return 'Name can only contain letters, spaces, hyphens, and apostrophes';
    }
    return '';
  };

  const validatePhone = (phone) => {
    if (!phone) {
      return ''; // Phone is optional
    }
    // Remove all non-digit characters for validation
    const digitsOnly = phone.replace(/\D/g, '');
    if (digitsOnly.length < 10) {
      return 'Phone number must be at least 10 digits';
    }
    if (digitsOnly.length > 15) {
      return 'Phone number must be less than 15 digits';
    }
    return '';
  };

  const validateField = (name, value) => {
    switch (name) {
      case 'email':
        return validateEmail(value);
      case 'name':
        return validateName(value);
      case 'phone':
        return validatePhone(value);
      default:
        return '';
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });

    // Validate field if it has been touched
    if (touched[name]) {
      const error = validateField(name, value);
      setFieldErrors({
        ...fieldErrors,
        [name]: error
      });
    }
  };

  const handleBlur = (e) => {
    const { name, value } = e.target;
    setTouched({
      ...touched,
      [name]: true
    });

    // Validate on blur
    const error = validateField(name, value);
    setFieldErrors({
      ...fieldErrors,
      [name]: error
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields
    const errors = {
      email: validateEmail(formData.email),
      name: validateName(formData.name),
      phone: validatePhone(formData.phone)
    };

    // Mark all fields as touched
    setTouched({
      email: true,
      name: true,
      phone: true
    });

    // Set field errors
    setFieldErrors(errors);

    // Check if there are any errors
    const hasErrors = Object.values(errors).some(error => error !== '');
    if (hasErrors) {
      setStatus('error');
      setErrorMessage('Please fix the errors above before submitting.');
      return;
    }

    setStatus('loading');
    setErrorMessage('');

    try {
      await joinWaitlist(formData);
      setStatus('success');
      setFormData({ email: '', name: '', phone: '', referralSource: '' });
      setFieldErrors({ email: '', name: '', phone: '' });
      setTouched({ email: false, name: false, phone: false });
    } catch (error) {
      setStatus('error');
      setErrorMessage(error.message || 'Failed to join waitlist. Please try again.');
    }
  };

  if (status === 'success') {
    return (
      <div className="waitlist-container">
        <header className="waitlist-nav">
          <div className="logo-section">
            <div className="logo-icon">R</div>
            <div className="logo-text">
              <div className="logo-title">Rivalis</div>
              <div className="logo-subtitle">AI-powered fitness competitions</div>
            </div>
          </div>
          <a href="/login" className="sign-in-link">Sign In</a>
        </header>
        <div className="waitlist-card success-card">
          <div className="success-icon">✓</div>
          <h1>You're on the list!</h1>
          <p>Thanks for joining the Rivalis iOS waitlist.</p>
          <p>We'll notify you as soon as the app is ready for beta testing.</p>
          <button
            className="btn-secondary"
            onClick={() => setStatus('idle')}
          >
            Sign up another person
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="waitlist-container">
      <header className="waitlist-nav">
        <div className="logo-section">
          <div className="logo-icon">R</div>
          <div className="logo-text">
            <div className="logo-title">Rivalis</div>
            <div className="logo-subtitle">AI-powered fitness competitions</div>
          </div>
        </div>
        <a href="/login" className="sign-in-link">Sign In</a>
      </header>

      <div className="waitlist-card">
        <div className="waitlist-header">
          <h1>Join the iOS Beta Waitlist</h1>
          <p>Be the first to access Rivalis on iOS.</p>
        </div>

        <div className="beta-features">
          <h2>WHAT'S COMING IN BETA</h2>
          <div className="features-grid">
            <div className="feature-item">
              <div className="feature-icon">
                <DumbbellIcon />
              </div>
              <h3>Personalized Workouts</h3>
              <p>AI-powered training plans tailored to your goals and fitness level.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <ChartIcon />
              </div>
              <h3>Progress Tracking</h3>
              <p>Track your workouts, streaks, and achievements in real-time.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <SwordsIcon />
              </div>
              <h3>Fitness Rivalries</h3>
              <p>Compete with friends and stay motivated through friendly competition.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <HeartIcon />
              </div>
              <h3>Health Integration</h3>
              <p>Seamless sync with Apple Watch and Health app data.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <TrendingIcon />
              </div>
              <h3>Performance Analytics</h3>
              <p>Detailed insights and recommendations to optimize your training.</p>
            </div>
            <div className="feature-item">
              <div className="feature-icon">
                <TargetIcon />
              </div>
              <h3>Fitness Testing</h3>
              <p>Assess your current fitness level and track improvements over time.</p>
            </div>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="waitlist-form">
          <div className="form-group">
            <label htmlFor="email">Email *</label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleChange}
              onBlur={handleBlur}
              required
              placeholder="your@email.com"
              disabled={status === 'loading'}
              className={fieldErrors.email && touched.email ? 'error' : ''}
            />
            {fieldErrors.email && touched.email && (
              <span className="field-error">{fieldErrors.email}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="name">Name</label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="Your name"
              disabled={status === 'loading'}
              className={fieldErrors.name && touched.name ? 'error' : ''}
            />
            {fieldErrors.name && touched.name && (
              <span className="field-error">{fieldErrors.name}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone (optional)</label>
            <input
              type="tel"
              id="phone"
              name="phone"
              value={formData.phone}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="+1 (555) 000-0000"
              disabled={status === 'loading'}
              className={fieldErrors.phone && touched.phone ? 'error' : ''}
            />
            {fieldErrors.phone && touched.phone && (
              <span className="field-error">{fieldErrors.phone}</span>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="referralSource">How did you hear about us?</label>
            <select
              id="referralSource"
              name="referralSource"
              value={formData.referralSource}
              onChange={handleChange}
              disabled={status === 'loading'}
            >
              <option value="">Select an option</option>
              <option value="friend">Friend or colleague</option>
              <option value="social">Social media</option>
              <option value="search">Search engine</option>
              <option value="ad">Advertisement</option>
              <option value="other">Other</option>
            </select>
          </div>

          {status === 'error' && (
            <div className="error-message">
              {errorMessage}
            </div>
          )}

          <button
            type="submit"
            className="btn-primary"
            disabled={status === 'loading'}
          >
            {status === 'loading' ? 'Joining...' : (
              <>
                Join Waitlist
                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: '8px' }}>
                  <path d="M5 12l6-6-6-6" />
                </svg>
              </>
            )}
          </button>
        </form>

        <div className="waitlist-footer">
          <p>Already have an account? <a href="/login">Sign in</a></p>
        </div>
      </div>
    </div>
  );
}
